import { BreakpointOverrides, SxProps } from '@mui/system';
import { isNil, isObject } from 'lodash-es';
import { ReactNode } from 'react';

import { RegisterOptions } from 'react-hook-form';
import { FormDefinitionHandlers } from '@/modules/form/hooks/useFormDefinitionHandlers';
import { HmisEnums } from '@/types/gqlEnums';
import {
  FormDefinitionJsonFieldsFragment,
  FormItem,
  GetAssessmentsForPopulationQuery,
  ItemType,
  PickListOption,
  SubmitFormMutation,
  ValidationError,
} from '@/types/gqlTypes';
// BACKEND FORM PROCESSOR EXPECTS THE '_HIDDEN' STRING VALUE, DO NOT CHANGE
export const HIDDEN_VALUE = '_HIDDEN';

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export enum ChangeType {
  User,
  System,
}

export type ItemChangedFn = (input: {
  linkId: string;
  value: any;
  type: ChangeType;
}) => void;

export type SeveralItemsChangedFn = (input: {
  values: FormValues;
  type: ChangeType;
}) => void;

// Types that are allowed to be submitted using SubmitForm.
// Exclude ReferralRequest type that is no longer supported but is still part of the schema.
export type SubmitFormAllowedTypes = Exclude<
  NonNullable<NonNullable<SubmitFormMutation['submitForm']>['record']>,
  { __typename?: 'ReferralRequest' }
>;

export type PickListArgs = {
  projectId?: string;
  clientId?: string;
  householdId?: string;
  enrollmentId?: string;
};
// Props to DynamicField. Need to put here to avoid circular deps.
export interface DynamicFieldProps {
  item: FormItem;
  itemChanged: ItemChangedFn;
  handlers?: FormDefinitionHandlers;
  value: any;
  disabled?: boolean;
  errors?: ValidationError[];
  inputProps?: DynamicInputCommonProps;
  horizontal?: boolean;
  pickListArgs?: PickListArgs;
  noLabel?: boolean;
  warnIfEmpty?: boolean;
  breakpoints?: BreakpointOverrides;
  localConstants?: LocalConstants;
}

export interface DynamicViewFieldProps {
  item: FormItem;
  value: any;
  horizontal?: boolean;
  noLabel?: boolean;
  disabled?: boolean;
  localConstants?: LocalConstants;
}

// Props accepted by all input components
export interface DynamicInputCommonProps {
  id?: string;
  disabled?: boolean;
  label?: ReactNode;
  ariaLabel?: string;
  error?: boolean;
  warnIfEmptyTreatment?: boolean;
  helperText?: ReactNode;
  min?: any;
  max?: any;
  placeholder?: string;
  breakpoints?: BreakpointOverrides;
  ariaLabelledBy?: string;
  maxWidth?: number | string; // maximum width of form element (including the label)
  inputWidth?: number; // fixed with of input element (does not affect label)
}

export interface DynamicViewItemCommonProps {
  id?: string;
  label?: ReactNode;
  children?: ReactNode;
}

export type OverrideableDynamicFieldProps = Optional<
  Omit<DynamicFieldProps, 'item' | 'value' | 'nestingLevel'>,
  'itemChanged' // allow groups to override item changed
>;

// Props accepted by all group components
export interface GroupItemComponentProps {
  item: FormItem;
  nestingLevel: number;
  renderChildItem: (
    item: FormItem,
    props?: OverrideableDynamicFieldProps,
    renderFn?: (children: ReactNode) => ReactNode
  ) => ReactNode;
  itemChanged?: ItemChangedFn;
  severalItemsChanged?: SeveralItemsChangedFn;
  visible?: boolean;
  viewOnly?: boolean;
  rowSx?: SxProps;
  handlers?: FormDefinitionHandlers;
}

export interface ViewGroupItemComponentProps {
  item: FormItem;
  nestingLevel: number;
  renderChildItem: (
    item: FormItem,
    props?: OverrideableDynamicFieldProps,
    renderFn?: (children: ReactNode) => ReactNode
  ) => ReactNode;
  values: Record<string, any>;
  visible?: boolean;
}

export enum FormActionTypes {
  Save,
  Submit,
  Validate,
  Discard,
  Navigate,
  Unlock,
}

export type FormValues = Record<string, any | null | undefined>;
export type ItemMap = Record<string, FormItem>;
export type LinkIdMap = Record<string, string[]>;
export type PartialLinkIdMap = Record<string, string[] | undefined>;
export type LocalConstants = Record<string, any>;
export type TypedObject = { __typename: string };

export const isHmisEnum = (k: string): k is keyof typeof HmisEnums => {
  return k in HmisEnums;
};

export const isQuestionItem = (item: FormItem): boolean =>
  ![ItemType.Display, ItemType.Group].includes(item.type);

export function isDate(value: any | null | undefined): value is Date {
  return (
    !isNil(value) &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    typeof value.getMonth === 'function'
  );
}

export function isPickListOption(
  value: any | null | undefined
): value is PickListOption {
  return (
    !isNil(value) &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    !!value.code
  );
}

export function isPickListOptionArray(
  value: any | null | undefined
): value is PickListOption[] {
  return (
    !isNil(value) &&
    Array.isArray(value) &&
    value.length > 0 &&
    isPickListOption(value[0])
  );
}

export const isTypedObject = (o: any): o is TypedObject => {
  return isObject(o) && o.hasOwnProperty('__typename');
};

export const isTypedObjectWithId = (
  o: any
): o is TypedObject & { id: string } => {
  return isTypedObject(o) && o.hasOwnProperty('id');
};

export type AssessmentForPopulation = NonNullable<
  GetAssessmentsForPopulationQuery['client']
>['assessments']['nodes'][0];

export type RhfRules = Omit<
  RegisterOptions,
  'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
>;

export type FormItemType = FormDefinitionJsonFieldsFragment['item'][number];
